import { logger } from "@/lib/logger";
import type {
  AnalyticsAdapter,
  AnalyticsEvent,
} from "@/modules/analytics/types";

const SCRIPT_ID = "plausible-script";

declare global {
  interface Window {
    plausible?: (
      event: string,
      options?: { props?: Record<string, unknown> },
    ) => void;
  }
}

export interface PlausibleAdapterOptions {
  domain: string;
  scriptUrl?: string;
}

export function createPlausibleAdapter({
  domain,
  scriptUrl = "https://plausible.io/js/script.js",
}: PlausibleAdapterOptions): AnalyticsAdapter {
  return {
    id: "plausible",
    requiresConsent: true,
    init(): void {
      if (typeof document === "undefined") return;
      if (document.getElementById(SCRIPT_ID)) return;
      const script = document.createElement("script");
      script.id = SCRIPT_ID;
      script.defer = true;
      script.src = scriptUrl;
      script.dataset.domain = domain;
      document.head.append(script);
      logger.info("analytics.plausible initialized");
    },
    page(path): void {
      if (
        typeof window === "undefined" ||
        typeof window.plausible !== "function"
      )
        return;
      window.plausible("pageview", { props: { path } });
    },
    track(event: AnalyticsEvent): void {
      if (
        typeof window === "undefined" ||
        typeof window.plausible !== "function"
      )
        return;
      window.plausible(event.name, { props: event.properties });
    },
    shutdown(): void {
      if (typeof document === "undefined") return;
      document.getElementById(SCRIPT_ID)?.remove();
    },
  };
}
